import React, { Fragment, useEffect, useRef, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Message from 'supportAssistant/components/Chat/Message'
import MessagesLoadingSkeleton from './MessagesLoadingSkeleton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLoggedIn, getFirstName } from 'selectors/accountSelectors'
import { MessageSenders } from 'supportAssistant/constants/messageSenders'
import moment from 'moment/moment'
import { ChatMessage } from 'supportAssistant/types/ChatMessage'
import { SocketContext } from '../../contexts/SocketContext'

const Container = styled.div`
  overflow-x: auto;
  height: 100%;
  margin-bottom: ${rem(4)};
  background-color: ${({ theme }) => theme.palette.neutral.eight};

  &::-webkit-scrollbar {
    display: none;
  }

  &--scroll-container {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

const StyledFooterBodyText = styled(BodyText)`
  word-break: break-word;
  white-space: pre-wrap;
  text-align: initial;
  margin-left: ${rem(8)};
`

const StyledDateBodyText = styled(BodyText)`
 margin-bottom: ${rem(24)};
 margin-top: ${rem(24)};
`

const StyledTimeBodyText = styled(BodyText)`
  margin-left: ${rem(34)};
  margin-bottom: ${rem(8)};
`

const MessagesContainerDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(12)};
`

const getWelcomeMessage = (firstName: string) => {
  return {
    sender: MessageSenders.CHATBOT,
    text: `Hi${firstName ? ` ${firstName}` : ''}, I'm LESA, the Luxury Escapes Support Assistant. I'm a virtual assistant here to help with any questions you have about your bookings and our policies.\n\nHow can I help you today?`,
  }
}

interface Props {
  messages: ChatMessage[];
  loadingMessages?: boolean;
  isTyping: boolean;
  chatCreatedAt: string;
  onMessageFeedbackSubmit: (feedback: { id: string, type: string }) => void;
}

function MessagesListContainer({
  messages,
  loadingMessages,
  isTyping,
  chatCreatedAt,
  onMessageFeedbackSubmit,
}: Props) {
  const userIsLoggedIn = useAppSelector(isLoggedIn)

  const { onLogin } = useContext(SocketContext)

  const firstName = useAppSelector(getFirstName)
  const welcomeMessage = getWelcomeMessage(firstName)

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, welcomeMessage])

  let previousDate = moment(chatCreatedAt).format('D MMMM YYYY')

  return (
    <Container>
      <MessagesContainerDiv>
        {loadingMessages && <MessagesLoadingSkeleton />}
        {!loadingMessages && <>
          <StyledDateBodyText variant="small" align="center">{moment(chatCreatedAt).format('D MMMM YYYY')}</StyledDateBodyText>
          <StyledTimeBodyText variant="small">{moment(chatCreatedAt).format('HH:MMa')}</StyledTimeBodyText>
          <Message message={welcomeMessage} footer={!userIsLoggedIn && <StyledFooterBodyText variant="small">To better assist you, please <TextLink weight="regular" onClick={onLogin} size="small">log in to Luxury Escapes</TextLink></StyledFooterBodyText>} />
          {messages?.map((message, index) => {
            const messageDate = moment(message.createdAt).format('D MMMM YYYY')

            if (messageDate !== previousDate) {
              previousDate = messageDate

              return (
                <Fragment key={`${index}`}>
                  <StyledDateBodyText variant="small" align="center">
                    {previousDate}
                  </StyledDateBodyText>
                  <Message
                    onMessageFeedbackSubmit={onMessageFeedbackSubmit}
                    message={message}
                    key={`${message.createdAt}-${index}`}
                  />
                </Fragment>
              )
            }

            return (
              <Message onMessageFeedbackSubmit={onMessageFeedbackSubmit} message={message} key={`${message.createdAt}-${index}`} />
            )
          })}
        </>}
        {isTyping && <Message message={{ isTyping }} />}
      </MessagesContainerDiv>
      <ScrollTo ref={scrollRef} />
    </Container>
  )
}

export default MessagesListContainer
